import { all, put, call, select, takeLatest, getContext } from 'redux-saga/effects';

import { Logger } from '../../utils/Logger';

import {
  newUsers,
  myTeamsList,
  REQUEST_INVITE_COMPANY,
  REQUEST_ADD_TEAM,
  REQUEST_INVITE_MORE_MEMBERS,
} from './types';

import {
  requestMyTeamsListSuccess,
  requestMyTeamsListFailure,
  requestAddTeamSuccess,
  requestInviteMoreUsersSuccess,
  requestInviteMoreUsersFailure,
} from './actions';

import { setTeam, setCompany, setTeamMembers } from '../teams/actions';

import { makeSelectUserCompanyId } from '../user/selectors';
import moveTeamMembersSaga from './moveTeamMembers/sagas';

const logger = new Logger('Saga:TeamsWizzard');

export function* requestMyTeamsListSaga() {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(authApiService.get, routerService.generate('api.teams.my_teams'));

    yield put(requestMyTeamsListSuccess(response.data));
  } catch (error) {
    logger.info('Failed to get the current user teams', error);
    yield put(requestMyTeamsListFailure(error));
  }
}

export function* requestInviteCompanySaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.teams.invite_company', { id: action.networkId }),
      action.payload,
    );

    action.promise && action.promise.resolve(response.data);
    yield put(setCompany(action.networkId, response.data));
  } catch (error) {
    logger.info('Failed to invite company', error);
    action.promise && action.promise.reject(error);
  }
}

export function* requestAddTeamSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  const currentUserCompanyId = yield select(makeSelectUserCompanyId());

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.teams.company_add_team', {
        id: action.companyId || currentUserCompanyId,
      }),
      action.payload,
    );

    action.promise && action.promise.resolve(response.data);
    yield put(requestAddTeamSuccess(response.data.id));
    yield put(setTeam(action.companyId || currentUserCompanyId, response.data));
  } catch (error) {
    logger.info('Failed to add a team', error);
    action.promise && action.promise.reject(error);
  }
}

export function* requestInviteMoreMembersSaga(action) {
  const authApiService = yield getContext('authApiService');
  const url = `${window._env_.PLATFORM_API_DOMAIN}/v1/auth/invite/sign-up`;

  try {
    const response = yield call(authApiService.post, url, action.payload);

    action.promise && action.promise.resolve(response.data);
    yield put(setTeamMembers(action.payload.team, response.data));
  } catch (error) {
    logger.info('Failed invite more team members', error);
    action.promise && action.promise.reject(error);
  }
}

export function* requestInviteUsersSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');
  const { promise, userEmails } = action;

  try {
    const response = yield call(authApiService.post, routerService.generate('api.send_invite'), {
      emails: userEmails,
    });

    yield put(requestInviteMoreUsersSuccess(userEmails));
    promise && promise.resolve(response.data);
  } catch (error) {
    logger.error('Failed to request invite users with the following error', error);
    yield put(requestInviteMoreUsersFailure(error));
    promise && promise.reject();
  }
}

export default function* teamsSaga() {
  return yield all([
    takeLatest(newUsers.request, requestInviteUsersSaga),
    takeLatest(myTeamsList.request, requestMyTeamsListSaga),
    takeLatest(REQUEST_INVITE_COMPANY, requestInviteCompanySaga),
    takeLatest(REQUEST_ADD_TEAM, requestAddTeamSaga),
    takeLatest(REQUEST_INVITE_MORE_MEMBERS, requestInviteMoreMembersSaga),
    moveTeamMembersSaga(),
  ]);
}
